import {useEffect, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useSearchParams} from "react-router-dom";
import {isEmpty} from "es-toolkit/compat";
import dayjs from "dayjs";

import {base64Decrypt} from "@/helpers/base64Decrypt.js";
import {closeBootstrapModal} from "@/helpers/closeBootstrapModal";
import {getBiomarkerLevelText} from "@/helpers/getBiomarkerLevelText.js";
import {getBiomarkerLevels} from "@/helpers/getBiomarkerLevels.js";
import {getVariantColor} from "@/helpers/getVariantColor.js";
import {openBootstrapModal} from "@/helpers/openBootstrapModal.js";
import Bar from "../Global/Charts/Bar/Bar";
import Badge from "@/components/Global/Badge/Badge";
import Button from "@/components/Global/Button/Button";
import Modal from "@/components/Global/Modal/Modal";
import RenderIf from "@/components/Global/RenderIf/RenderIf";
import {updateContentNudgeSent} from "@/features/notifications";

import {config} from "@/config";
import styles from "./Notifications.module.scss";
import {selectResultsHistory} from "@/features/results/resultsSelectors";

export default function Notifications() {
  const resultHistory = useSelector(selectResultsHistory);
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const convertStringToObject = content => {
    if (!content || content.indexOf("[") === -1) return null;

    return JSON.parse(
      content
        .replaceAll("[", "{")
        .replaceAll("]", "}")
        .replaceAll("=>", ":")
        .replace(/[\r\n]+/g, "")
        .replace(/&#39;/g, "'"),
    );
  };

  const {
    id,
    icon = "",
    title = "",
    main = "",
    readmore_btn: readMoreBtn = "",
    readmore_link: readMoreLink = "",
    bid = 0,
  } = useMemo(() => {
    const contentNudges = searchParams.get("cn");

    if (contentNudges) {
      try {
        const contentNudgesDecrypted = base64Decrypt(contentNudges.replaceAll(" ", "+"));
        return convertStringToObject(contentNudgesDecrypted);
      } catch (e) {
        return {};
      }
    }

    return {};
  }, [searchParams]);

  const latestBiomarkerResult = useMemo(() => {
    if (!bid) return {};

    return (
      resultHistory
        .filter(result => result.biomarkerId == bid)
        .sort((a, b) => {
          return dayjs(b.orderCompleteDate).diff(dayjs(a.orderCompleteDate), "days");
        })[0] || {}
    );
  }, [resultHistory, bid]);

  const ranges = useMemo(() => {
    if (isEmpty(latestBiomarkerResult)) return [];
    return getBiomarkerLevels(latestBiomarkerResult);
  }, [latestBiomarkerResult]);

  const rangeTarget = useMemo(() => {
    if (isEmpty(latestBiomarkerResult)) return [];
    return [
      {x: "start", y: latestBiomarkerResult.targetLow || 0, percentage: latestBiomarkerResult.targetLowP || 0},
      {x: "end", y: latestBiomarkerResult.targetHigh || 0, percentage: latestBiomarkerResult.targetHighP || 0},
    ];
  }, [latestBiomarkerResult]);

  const readMoreLinkSanitised = useMemo(() => {
    if (!readMoreLink) return readMoreLink;

    const domains = ["app.biomarkertracking", "app.forthwithlife", "app.sportsbloodtests"];
    const includeDomain = domains.some(domain => readMoreLink.toLowerCase().includes(domain));

    if (readMoreLink && includeDomain) {
      const url = new URL(readMoreLink);
      const pathname = url.pathname;
      const search = url.search;
      return pathname + search;
    }

    return readMoreLink;
  }, [readMoreLink]);

  const handleCloseModal = () => {
    closeBootstrapModal("notification-modal");
    setSearchParams(prev => prev.delete("cn") || prev, {replace: true});
  };

  useEffect(() => {
    if (main && title) {
      openBootstrapModal("notification-modal");
      id && dispatch(updateContentNudgeSent(id));
    }

    return () => {
      if (main && title) {
        closeBootstrapModal("notification-modal");
      }
    };
  }, [main, title, id]);

  return (
    <>
      <Modal
        id="notification-modal"
        variant="bottom"
        className={`container-fluid px-0 ${styles.content}`}
        staticModal={true}
        onClose={handleCloseModal}>
        <header className="row mx-0 align-items-center justify-content-center mb-3">
          <div className="col-12 g-0 position-relative">
            <p className={`text-center py-2 ${styles.brand}`}>
              {config.websiteId === 2 ? "Edge Hub" : "Forth Health Hub"}
            </p>
            <button
              type="button"
              className={`${styles.closeBtn} btn-close`}
              data-bs-dismiss="modal"
              aria-label="Close"></button>
          </div>
          {/* <div className={`col-12 ${!icon ? "d-none" : "d-flex justify-content-center"}`}>
            <i
              className={`text-center text-balance mb-0 ${styles.icon}`}
              dangerouslySetInnerHTML={{__html: `&#x${icon};`}}></i>
          </div> */}
          <div className="col-auto">
            <h2 className="text-balance mb-0" dangerouslySetInnerHTML={{__html: title}}></h2>
          </div>
        </header>
        <main className="px-3 mb-4">
          <p className="text-pretty lh-base" dangerouslySetInnerHTML={{__html: main}}></p>
          <RenderIf isTrue={!isEmpty(latestBiomarkerResult)}>
            <div className="pt-1">
              <h3 className="text-center text-balance pb-2">{latestBiomarkerResult.biomarkerName}</h3>
              <Bar
                score={latestBiomarkerResult.biomarkerResultScore}
                scorePercentage={latestBiomarkerResult.biomarkerResultScoreP}
                target={rangeTarget}
                ranges={ranges}
                rangeStatus={latestBiomarkerResult.rangeStatus}
                unit={latestBiomarkerResult.biomarkerUnit}
                showTarget={config.websiteId === 1 && latestBiomarkerResult.showHCTarget}>
                <p className={`${styles.chartLegend} text-center`}>
                  <span className={styles.chartScore}>{latestBiomarkerResult.biomarkerResultScoreDisplay}</span>{" "}
                  {latestBiomarkerResult.biomarkerUnit && `(${latestBiomarkerResult.biomarkerUnit})`}{" "}
                  <Badge variant={getVariantColor(latestBiomarkerResult.rangeStatus)} size="lg" className="fw-semibold">
                    {getBiomarkerLevelText(latestBiomarkerResult.rangeStatus)}
                  </Badge>
                </p>
              </Bar>
            </div>
            <small className="text-muted d-block text-center text-pretty">
              <i>This is for information only and is not intended for medical diagnosis or advice.</i>
              <a
                href={`https://www.${config.websiteId === 2 ? "sportsbloodtests" : "forthwithlife"}.co.uk/?appbrowser=true`}
                target="_blank"
                className="d-block"
                rel="noreferrer">
                www.{config.websiteId === 2 ? "sportsbloodtests" : "forthwithlife"}.co.uk
              </a>
            </small>
          </RenderIf>
        </main>
        <footer className="row mx-0 gy-2 px-3 mb-3">
          <RenderIf isTrue={readMoreBtn && readMoreLink}>
            <Button
              href={readMoreLinkSanitised || readMoreLink}
              variant="primary"
              className="py-3 col-12 d-block text-center">
              {readMoreBtn}
            </Button>
          </RenderIf>
          <Button
            data-bs-dismiss="modal"
            variant="link-default"
            className="py-3 col-12 d-block"
            onClick={handleCloseModal}>
            Close
          </Button>
        </footer>
      </Modal>
    </>
  );
}
